import { useTranslation } from 'react-i18next';
import { useServiceCategoriesPrivate, useUpdateServiceCategoryRank } from '@/hooks/api-hooks';
import { NumberInput, Loader, Alert, Tooltip, Badge } from '@mantine/core';
import { IconEyeClosed, IconEyeOff, IconWorld } from '@tabler/icons-react';

export default function ServiceCategoriesRanks() {
    const { t } = useTranslation();
    const { data: categoriesData, isLoading, error } = useServiceCategoriesPrivate();
    const updateRankMutation = useUpdateServiceCategoryRank();

    const handleRankChange = (id: string | number, newRank: number | string) => {
        let rankValue: number | null = null;
        if (typeof newRank === 'number') {
            rankValue = newRank;
        } else if (newRank !== '') {
            rankValue = parseInt(newRank as string, 10);
        }
        updateRankMutation.mutate({ id, rank: rankValue });
    };

    if (isLoading) return <Loader size="sm" color='black' />;
    if (error) return <Alert color="red">{error.message}</Alert>;

    const categories = categoriesData?.data || [];

    return (
        <div className="solid-box-border p-5">
            <div className='font-semibold text-gray-800 text-xl mb-4'>
                {t('service_categories')} ({t('rank')})
            </div>
            <div className="flex flex-wrap gap-4">
                {categories?.map((cat: any) => {
                    const isInternal = cat.rank === 0 || cat.rank === null;
                    return (
                        <div
                            key={cat.id}
                            className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg bg-gray-50/50 hover:bg-white hover:shadow-sm transition-all min-w-[260px] flex-1 max-w-[350px]">
                            <div className="flex-1 min-w-0">
                                <div className="font-semibold text-[15px] text-gray-800 truncate mb-1">
                                    {t(cat.category)}
                                </div>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                    {isInternal ? (
                                        <Tooltip label={t('internal_use')} withArrow>
                                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-500 bg-gray-200/60 px-2 py-0.5 rounded-full">
                                                <IconEyeClosed size={11} stroke={2.5} />
                                                {t('internal_use')}
                                            </span>
                                        </Tooltip>
                                    ) : (
                                        <Tooltip label={t('published')} withArrow>
                                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                                                <IconWorld size={11} stroke={2} />
                                                {t('published')}
                                            </span>
                                        </Tooltip>
                                    )}
                                </div>
                            </div>
                            <div className="w-20 shrink-0">
                                <NumberInput
                                    size="sm"
                                    radius="md"
                                    placeholder={t('rank')}
                                    defaultValue={cat.rank}
                                    onBlur={(e) => handleRankChange(cat.id, e.currentTarget.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleRankChange(cat.id, e.currentTarget.value);
                                        }
                                    }}
                                    min={0}
                                    allowDecimal={false}
                                    allowNegative={false}
                                    disabled={updateRankMutation.isPending}
                                    hideControls
                                    classNames={{
                                        input: 'text-center font-semibold text-gray-700'
                                    }}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
